import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_EMBLEM } from "@/lib/brand";

export const Navbar = ({ section }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!section) return null;

  const links = section.metadata?.links || [];
  const logo = section.image || LOGO_EMBLEM;

  const go = (rawHref) => {
    const href = String(rawHref || "").trim();
    setOpen(false);
    if (!href) return;

    const sectionId = href.replace(/^\/?#?/, "");
    const isHomepageSection = ["events", "roster", "manifesto", "join"].includes(sectionId);

    if (href.startsWith("#") || isHomepageSection) {
      const id = href.startsWith("#") ? href.slice(1) : sectionId;
      const scrollToSection = () => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `#${id}`);
      };

      // Let the mobile menu start closing before scrolling the page underneath it.
      window.requestAnimationFrame(scrollToSection);
      return;
    }

    window.location.href = href;
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/80 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img
            src={logo}
            alt="Susegado Strike"
            className="h-12 md:h-14 w-auto transition-transform duration-300 group-hover:scale-105"
          />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((link) => (
            <button
              key={`${link.label}-${link.href}`}
              data-testid={`nav-${String(link.label).toLowerCase()}`}
              onClick={() => go(link.href)}
              className="font-oswald uppercase text-sm tracking-[0.2em] text-[#A3A3A3] hover:text-[#E85A24] transition-colors duration-300"
            >
              {link.label}
            </button>
          ))}
          {section.button_label && (
            <button
              data-testid="nav-tickets"
              onClick={() => go(section.button_url)}
              className="font-oswald uppercase text-sm tracking-[0.2em] px-5 py-2 border border-[#E85A24] text-[#E85A24] hover:bg-[#E85A24] hover:text-black transition-colors duration-300"
            >
              {section.button_label}
            </button>
          )}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden flex flex-col gap-1.5"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          <span className={`h-0.5 w-7 bg-white transition-transform ${open ? "translate-y-2 rotate-45" : ""}`} />
          <span className={`h-0.5 w-7 bg-white transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`h-0.5 w-7 bg-white transition-transform ${open ? "-translate-y-2 -rotate-45" : ""}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((link) => (
                <button
                  key={`${link.label}-${link.href}`}
                  data-testid={`nav-mobile-${String(link.label).toLowerCase()}`}
                  onClick={() => go(link.href)}
                  className="text-left font-oswald uppercase text-lg tracking-widest text-[#FAFAFA] hover:text-[#E85A24]"
                >
                  {link.label}
                </button>
              ))}
              {section.button_label && (
                <button
                  data-testid="nav-mobile-tickets"
                  onClick={() => go(section.button_url)}
                  className="self-start font-oswald uppercase text-sm tracking-[0.2em] px-5 py-2 border border-[#E85A24] text-[#E85A24] hover:bg-[#E85A24] hover:text-black transition-colors duration-300"
                >
                  {section.button_label}
                </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
